import { useEffect, useEffectEvent, useRef } from 'react'
import { removeDraft, sessionDraftStorage, writeDraft, type DraftScope, type StoredDraft } from './draftStorage'

export const DRAFT_SAVE_DELAY_MS = 300

export interface PersistedDraft {
  scope: DraftScope
  text: string
  suggestionId: string | null
  inReplyToMessageId: string | null
}

const persist = ({ scope, text, suggestionId, inReplyToMessageId }: PersistedDraft): void => {
  const storage = sessionDraftStorage()
  if (text.length === 0) return removeDraft(storage, scope)
  const draft: StoredDraft = { text, suggestionId, inReplyToMessageId }
  writeDraft(storage, scope, draft)
}

export const usePersistedDraft = (draft: PersistedDraft): void => {
  const pendingRef = useRef(false)
  const save = useEffectEvent(() => {
    pendingRef.current = false
    persist(draft)
  })
  const { scope, text, suggestionId, inReplyToMessageId } = draft
  const key = `${scope.userId}|${scope.tenantId}|${scope.itemId}`

  useEffect(() => {
    if (text.length === 0) return save()
    pendingRef.current = true
    const timer = setTimeout(save, DRAFT_SAVE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [key, text, suggestionId, inReplyToMessageId])

  useEffect(
    () => () => {
      if (pendingRef.current) save()
    },
    [],
  )

  const hasText = text.length > 0
  useEffect(() => {
    if (!hasText) return
    const warn = (event: BeforeUnloadEvent) => {
      save()
      event.preventDefault()
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [hasText])
}
